import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, X, SendHorizontal } from "lucide-react";
import { API } from "@/lib/site";

const GREETING = {
  role: "assistant",
  text: "Sawubona! I'm the NGCONDE dispatch assistant. Ask me about our bakkie fleet, parcel capacity, the provinces we run, or type your routes and volumes for an indicative setup.",
};

const CHIPS = [
  "What do you transport?",
  "Which provinces do you cover?",
  "I need 2 bakkies for daily runs",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem("ngconde-chat-session");
    if (!sid) {
      sid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("ngconde-chat-session", sid);
    }
    return sid;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: msg }),
      });
      if (!res.ok || !res.body) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(
          typeof detail.detail === "string" ? detail.detail : "Assistant unavailable right now.",
        );
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          const evt = JSON.parse(payload);
          if (evt.error) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", text: evt.error };
              return c;
            });
          } else if (evt.delta) {
            setMessages((m) => {
              const c = [...m];
              const last = c[c.length - 1];
              c[c.length - 1] = { ...last, text: last.text + evt.delta };
              return c;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          text: e?.message || "Assistant unavailable — WhatsApp 071 243 1886 for instant help.",
        };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-widget-toggle"
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#FF6B00] text-[#080C14] shadow-[0_8px_30px_rgba(255,107,0,0.45)] transition-all hover:scale-105 hover:bg-[#E05E00] active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-5 z-40 flex h-[540px] max-h-[calc(100svh-140px)] w-[calc(100vw-40px)] max-w-sm flex-col overflow-hidden rounded-2xl border border-white/15 bg-[#0D1424] shadow-2xl"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-[#080C14] px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FF6B00]/15 text-[#FF6B00]">
                <Bot className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-sm font-extrabold uppercase tracking-tight text-white">
                  NGCONDE Assistant
                </p>
                <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#10B981]">
                  <span className="live-dot h-1.5 w-1.5 rounded-full bg-[#10B981]" /> AI-powered
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={m.role === "user" ? "chat-message-user" : "chat-message-assistant"}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-[#FF6B00] text-[#080C14]"
                      : "bg-[#131C31] text-slate-200"
                  }`}
                >
                  {m.text || (streaming && i === messages.length - 1 ? "..." : "")}
                </div>
              ))}
              {messages.length === 1 && !streaming && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {CHIPS.map((chip) => (
                    <button
                      key={chip}
                      onClick={() => send(chip)}
                      data-testid="chat-quick-chip"
                      className="rounded-full border border-[#FF6B00]/40 px-3 py-1.5 text-xs text-[#FF6B00] transition-colors hover:bg-[#FF6B00] hover:text-[#080C14]"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-white/10 bg-[#080C14] p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about routes, fleet, coverage..."
                data-testid="chat-input"
                className="flex-1 rounded-full border border-white/15 bg-[#0D1424] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#FF6B00]"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-btn"
                aria-label="Send message"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF6B00] text-[#080C14] transition-all hover:bg-[#E05E00] active:scale-95 disabled:opacity-50"
              >
                <SendHorizontal className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
